import { Badge, Box, Group, Stack, Text } from "@mantine/core";
import { IconHeart } from "@tabler/icons-react";
import type { AvatarState } from "../../shared/types";

interface Props {
  players: AvatarState[];
  bots: AvatarState[];
  selfId: string | null;
  friendAddresses?: Set<string>;
  onSelectPlayer?: (avatar: AvatarState) => void;
  onHoverPlayer?: (avatarId: string | null) => void;
}

function dotColor(avatar: AvatarState, isSelf: boolean): string {
  if (avatar.color && avatar.color !== "#000000") return avatar.color;
  if (isSelf) return "#ff922b";
  return "#888888";
}

export function PlayerList({
  players,
  bots,
  selfId,
  friendAddresses,
  onSelectPlayer,
  onHoverPlayer,
}: Props) {
  const all = [...players, ...bots];

  // Sort: friends first, then self, then others
  const sorted = friendAddresses
    ? [...all].sort((a, b) => {
        const aIsSelf = a.id === selfId;
        const bIsSelf = b.id === selfId;
        const aIsFriend = friendAddresses.has(a.address);
        const bIsFriend = friendAddresses.has(b.address);
        if (aIsFriend && !bIsFriend) return -1;
        if (!aIsFriend && bIsFriend) return 1;
        if (aIsSelf && !bIsSelf) return -1;
        if (!aIsSelf && bIsSelf) return 1;
        return 0;
      })
    : all;

  const clickable = Boolean(onSelectPlayer);

  return (
    <Stack gap={3}>
      {sorted.map((avatar) => {
        const isSelf = avatar.id === selfId;
        const isFriend = friendAddresses?.has(avatar.address) ?? false;
        return (
          <Group
            key={avatar.id}
            gap="xs"
            py={3}
            px={4}
            style={{
              borderRadius: 6,
              cursor: clickable ? "pointer" : "default",
              transition: "background 0.1s",
            }}
            onMouseEnter={(e) => {
              if (clickable) {
                e.currentTarget.style.background = "rgba(0,0,0,0.04)";
              }
              onHoverPlayer?.(avatar.id);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "";
              onHoverPlayer?.(null);
            }}
            onClick={() => onSelectPlayer?.(avatar)}
          >
            <Box
              w={8}
              h={8}
              style={{
                borderRadius: "50%",
                background: dotColor(avatar, isSelf),
                flexShrink: 0,
              }}
            />
            <Text
              size="xs"
              fw={isSelf || isFriend ? 700 : 400}
              style={{ flex: 1 }}
            >
              {avatar.name}
              {isSelf ? " (you)" : ""}
            </Text>
            {isFriend && (
              <IconHeart
                size={12}
                color="#ff922b"
                fill="#ff922b"
                style={{ flexShrink: 0 }}
              />
            )}
            {avatar.role !== "player" && (
              <Text size="xs" c="dimmed">
                {avatar.role}
              </Text>
            )}
          </Group>
        );
      })}
      {sorted.length === 0 && (
        <Text size="xs" c="dimmed">
          No one here yet.
        </Text>
      )}
    </Stack>
  );
}
